import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { getAppMarket } from '../../services/MarketService';
import {
    entitlementConsoleService as entitlementSvc,
    tierLabel,
    statusLabel,
    type TenantEntitlementDetail,
    type EntitlementCatalog,
} from '../../services/entitlementConsole';

interface FeatureToggleModalProps {
    tenantId: string;
    onClose: () => void;
}

const UK_MODULES = [
    { key: 'dashboard', label: 'Dashboard & Stats', description: 'Core dashboard features' },
    { key: 'properties', label: 'Property Management', description: 'Listing and editing properties' },
    { key: 'certificates', label: 'Compliance & Certificates', description: 'Document tracking and safety checks' },
    { key: 'evictions', label: 'Eviction Workflow', description: 'Automated legal notice generation' },
    { key: 'finance', label: 'Finance & VAT', description: 'Rent tracking and financial logs' },
    { key: 'arrears', label: 'Arrears Detection', description: 'Early warning system for late payments' },
    { key: 'propertyGroups', label: 'Housing Pro: Regions & Groups', description: 'Hierarchical property grouping (Enterprise)' },
    { key: 'evidenceVault', label: 'Evidence Vault', description: 'Immutable audit trail of documents' },
    { key: 'calendar', label: 'Compliance Calendar', description: 'Unified event and deadline calendar' },
    { key: 'aml', label: 'AML Anti-Money Laundering', description: 'Tenant vetting and AML trails' },
    { key: 'agreements', label: 'Tenancy Agreements', description: 'Agreement management and NLP analysis' },
    { key: 'communications', label: 'Tenant Communications', description: 'Automated reminders and messaging' },
    { key: 'complianceReportingWidget', label: 'Compliance Reporting Widget', description: 'Portfolio health, TSM and complaints reporting card on the main Dashboard' },
];

// F-307 — US feature set for the Texas market (human directive: Feature
// Management must show relevant US features). Keys map to the same tenant
// feature flags; labels/descriptions are US-facing. UK-specific AML is
// dropped; Certificates becomes the digital vault; Evictions becomes the
// Texas notices set.
const US_MODULES = [
    { key: 'dashboard', label: 'Dashboard & Stats', description: 'Core dashboard and portfolio summary' },
    { key: 'properties', label: 'Property Management', description: 'Listing and editing properties' },
    { key: 'certificates', label: 'Document Vault & Validation', description: 'Digital vault: leases, disclosures, registration documents (up to 5 slots)' },
    { key: 'evictions', label: 'Notices & Eviction (SB 38)', description: 'Notice to Vacate, ASEC remediation, SB 38 generators and timelines' },
    { key: 'finance', label: 'Rent & Finance', description: 'Rent tracking, deposits and financial logs' },
    { key: 'arrears', label: 'Past-Due Rent Detection', description: 'Early warning system for late payments' },
    { key: 'propertyGroups', label: 'Housing Pro: Regions & Groups', description: 'Hierarchical property grouping (Enterprise)' },
    { key: 'evidenceVault', label: 'Evidence Vault', description: 'Immutable audit trail of documents' },
    { key: 'calendar', label: 'Compliance Calendar', description: 'Unified event and deadline calendar' },
    { key: 'agreements', label: 'Lease Agreements & NLP', description: 'Lease management and clause analysis' },
    { key: 'communications', label: 'Tenant Communications', description: 'Automated reminders and messaging' },
    { key: 'complianceReportingWidget', label: 'Compliance Reporting Widget', description: 'Portfolio health reporting card on the main Dashboard' },
];

const FeatureToggleModal: React.FC<FeatureToggleModalProps> = ({ tenantId, onClose }) => {
    // F-307: market decides which feature set the selector shows.
    const MODULES = getAppMarket() === 'texas' ? US_MODULES : UK_MODULES;
    const [features, setFeatures] = useState<any>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // F-305: US entitlement alignment — the same feature selector also speaks
    // the F-303 entitlement engine for Texas-lane tenants. Non-fatal: tenants
    // without a stored US record (UK lane) never see the section.
    const [usDetail, setUsDetail] = useState<TenantEntitlementDetail | null>(null);
    const [usCatalog, setUsCatalog] = useState<EntitlementCatalog | null>(null);
    const [usBusy, setUsBusy] = useState<string | null>(null);
    const [usConflict, setUsConflict] = useState<string | null>(null);
    const [usError, setUsError] = useState<string | null>(null);

    const refreshUsDetail = async (id: string) => {
        const detail = await entitlementSvc.getTenantDetail(id);
        setUsDetail(detail);
    };

    useEffect(() => {
        const fetchFeatures = async () => {
            try {
                // We use the adminApi to hit the super-admin endpoint
                const res = await adminApi.get(`/api/super-admin/tenants/${tenantId}/features`);
                if (res.data?.success) {
                    setFeatures(res.data.data || {});
                }
            } catch (err) {
                console.error('[FeatureToggle] Fetch error:', err);
                // Fallback to empty if not found
            } finally {
                setLoading(false);
            }
        };
        const fetchUsEntitlements = async () => {
            try {
                const [detail, catalog] = await Promise.all([
                    entitlementSvc.getTenantDetail(tenantId),
                    entitlementSvc.getCatalog(),
                ]);
                // The backend seeds a default record (freemium/restricted, no
                // overrides) for ANY tenant on first GET — so "has a record"
                // is not the Texas-lane signal. Show the section only when the
                // tenant carries non-default US state; a UK tenant's seeded
                // default keeps the modal unchanged.
                const rec = detail?.record;
                const hasUsState = !!rec && (
                    (rec.tier && rec.tier !== 'freemium') ||
                    (rec.subscriptionStatus && rec.subscriptionStatus !== 'restricted') ||
                    Object.keys(rec.premiumOverrides || {}).length > 0 ||
                    rec.refundVoid === true
                );
                if (hasUsState) {
                    setUsDetail(detail);
                    setUsCatalog(catalog);
                }
            } catch (err: any) {
                console.warn('[FeatureToggle] US entitlement lookup unavailable (non-fatal):', err?.message);
            }
        };
        fetchFeatures();
        fetchUsEntitlements();
    }, [tenantId]);

    // `resolved` is a normalized OBJECT whose `entitlements` is a guaranteed
    // string[] — the prod blank-page crash called .some() on the raw object.
    const usEffectiveOn = (entitlementId: string) =>
        !!usDetail?.resolved?.entitlements?.includes(entitlementId);

    const handleUsToggle = async (entitlementId: string) => {
        setUsBusy(entitlementId);
        setUsConflict(null);
        setUsError(null);
        try {
            if (usEffectiveOn(entitlementId)) {
                await entitlementSvc.setEntitlementOverride(tenantId, entitlementId, false, 'feature selector revoke');
            } else {
                await entitlementSvc.setEntitlementOverride(tenantId, entitlementId, true, 'feature selector grant');
            }
            await refreshUsDetail(tenantId);
        } catch (err: any) {
            if (err?.status === 409) {
                setUsConflict(err.message);
            } else {
                setUsError(err?.response?.data?.message || err?.message || 'Failed to update entitlement');
            }
        } finally {
            setUsBusy(null);
        }
    };

    const handleUsReset = async (entitlementId: string) => {
        setUsBusy(entitlementId);
        setUsConflict(null);
        setUsError(null);
        try {
            await entitlementSvc.clearEntitlementOverride(tenantId, entitlementId);
            await refreshUsDetail(tenantId);
        } catch (err: any) {
            setUsError(err?.response?.data?.message || err?.message || 'Failed to reset override');
        } finally {
            setUsBusy(null);
        }
    };

    const handleToggle = (key: string) => {
        setFeatures((prev: any) => ({
            ...prev,
            [key]: {
                ...prev[key],
                enabled: !prev[key]?.enabled
            }
        }));
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await adminApi.put(`/api/super-admin/tenants/${tenantId}/features`, { modules: features });
            alert('Tenant features updated successfully.');
            onClose();
        } catch (err) {
            alert('Failed to update features.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                <header className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                    <div>
                        <h2 className="text-xl font-black text-slate-900 tracking-tight">Feature Management</h2>
                        <p className="text-xs text-slate-500 font-mono">{tenantId}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </header>

                <div className="p-6 max-h-[60vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex flex-col items-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-900 border-t-transparent mb-2"></div>
                             <p className="text-sm text-gray-500 italic">Reading tenant configuration...</p>
                        </div>
                    ) : (
                        <div className="grid gap-3">
                            {MODULES.map(module => (
                                <div 
                                    key={module.key}
                                    className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                                        features[module.key]?.enabled 
                                            ? 'bg-emerald-50 border-emerald-100' 
                                            : 'bg-white border-gray-100'
                                    }`}
                                >
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h4 className={`text-sm font-bold ${features[module.key]?.enabled ? 'text-emerald-900' : 'text-slate-700'}`}>
                                                {module.label}
                                            </h4>
                                            {module.key.includes('propertyGroups') && (
                                                <span className="bg-indigo-600 text-white text-[9px] font-black px-1.5 py-0.5 rounded tracking-tighter uppercase">PRO</span>
                                            )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-0.5">{module.description}</p>
                                    </div>
                                    
                                    <button 
                                        onClick={() => handleToggle(module.key)}
                                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                                            features[module.key]?.enabled ? 'bg-emerald-500' : 'bg-gray-200'
                                        }`}
                                    >
                                        <span 
                                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                features[module.key]?.enabled ? 'translate-x-5' : 'translate-x-0'
                                            }`} 
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {usDetail && usCatalog && (
                        <div className="mt-6 pt-4 border-t border-gray-100">
                            <div className="flex items-center justify-between mb-2">
                                <div>
                                    <h3 className="text-sm font-black text-slate-900 tracking-tight">US Entitlements (Texas lane)</h3>
                                    <p className="text-xs text-slate-500">
                                        F-303 engine · tier <strong>{tierLabel(usDetail.resolved?.tier || usDetail.record?.tier)}</strong> ·{' '}
                                        {statusLabel(usDetail.resolved?.subscriptionStatus || usDetail.record?.subscriptionStatus)} — toggles write live overrides (cross-tier grants are rejected by billing).
                                    </p>
                                </div>
                            </div>
                            {usConflict && <div className="mb-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"><strong>409 — grant rejected:</strong> {usConflict}</div>}
                            {usError && <div className="mb-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{usError}</div>}
                            <div className="grid gap-2">
                                {Object.values(usCatalog.entitlements).map((ent) => {
                                    const on = usEffectiveOn(ent.id);
                                    const override = usDetail.record?.premiumOverrides?.[ent.id];
                                    return (
                                        <div
                                            key={ent.id}
                                            className={`flex items-center justify-between p-3 rounded-xl border transition-all ${on ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-gray-100'}`}
                                        >
                                            <div>
                                                <p className="text-sm font-bold text-slate-800">{ent.name}</p>
                                                <p className="text-xs text-slate-500">
                                                    {tierLabel(ent.planAccess)}+ · {ent.metered ? 'metered per use' : 'flat included'}
                                                    {override !== undefined ? ` · admin override: ${override ? 'granted' : 'revoked'}` : ''}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {override !== undefined && (
                                                    <button
                                                        onClick={() => handleUsReset(ent.id)}
                                                        disabled={usBusy !== null}
                                                        className="text-xs font-bold text-gray-500 hover:text-gray-700 disabled:opacity-40"
                                                        aria-label={`Reset ${ent.id} to tier default`}
                                                    >
                                                        Reset
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleUsToggle(ent.id)}
                                                    disabled={usBusy === ent.id}
                                                    aria-label={`Toggle ${ent.id}`}
                                                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${on ? 'bg-emerald-500' : 'bg-gray-200'} disabled:opacity-50`}
                                                >
                                                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${on ? 'translate-x-5' : 'translate-x-0'}`} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                <footer className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
                    <button 
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-bold text-gray-500 hover:text-gray-700 transition"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={saving || loading}
                        className="px-6 py-2 bg-slate-900 text-white rounded-lg text-sm font-bold hover:bg-slate-800 transition disabled:opacity-50 flex items-center gap-2 shadow-lg"
                    >
                        {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : 'Apply Changes'}
                    </button>
                </footer>
            </div>
        </div>
    );
};

export default FeatureToggleModal;
